import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Form, Row, Col, Button, Card, Image } from 'react-bootstrap';
import worldLanguages from './data/worldLanguages'
import continents from './data/continents'
import countries from './data/countries'
import Select from 'react-select';
import icValidationError from './assets/img/ic-danger.svg';
import currencies from './data/currencies'
import icCity from './assets/img/ic-city.svg';
import icNumber from './assets/img/ic-number.svg';
import icFlag from './assets/img/ic-banner.svg';
import icGlobe from './assets/img/ic-globe.svg';
import icLink from './assets/img/ic-link-square.svg';

const CountryAddGeneral = forwardRef(({ formData, handleGeneralSubmit, handleChange, errors, validateURL, isEditPage, isReviewPage }, ref) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    
    const [localErrors, setLocalErrors] = useState({ languagesSpoken: '', countryName: '', citizensNumber: '', url: '', continent: '', currency: '' });
    
    const [touched, setTouched] = useState({ languagesSpoken: false, countryName: false, citizensNumber: false, url: false, continent: false, currency: false });

    const [urlInput, setUrlInput] = useState(formData.url || '');

    useEffect(() => {
        setUrlInput(formData.url || ''); // ✅ Ensure urlInput updates when formData changes
    }, [formData.url]);

    // ✅ normalize + validate
    const normalizeUrl = (raw) => {
        const s = (raw || "").trim();
        if (!s) return null;

        // If no scheme, default to https://
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(s) ? s : `https://${s}`;

        try {
            const u = new URL(withScheme);
            // Only allow http/https
            if (!["http:", "https:"].includes(u.protocol)) return null;
            return u.toString();
        } catch {
            return null;
        }
    };

    const safeHref = normalizeUrl(urlInput);

    const countryOptions = countries.map(c => ({ value: c.id, label: c.name }));

    // Handle URL field change
    const handleURLChange = (e) => {
        const { value } = e.target;
        setUrlInput(value);
    
        clearTimeout(window.urlValidationTimeout);
        window.urlValidationTimeout = setTimeout(() => {
            if (value && !validateURL(value)) {
                setLocalErrors((prevErrors) => ({ ...prevErrors, url: 'Please enter a valid URL.' }));
            } else {
                setLocalErrors((prevErrors) => ({ ...prevErrors, url: '' }));
                handleChange({ target: { name: 'url', value } });  // Make sure this updates the main formData
            }
        }, 800);
    };
    
    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        if (!formData.country?.name?.trim() || !formData.url?.trim()) {
            alert('Country name and website are required.');
            return;
        }

        if (typeof handleGeneralSubmit === 'function') { // ✅ Ensure it's a function before calling
            await handleGeneralSubmit(e);
            setIsEditing(false); // ✅ Exit edit mode after saving
        } else {
            console.error('Error: handleGeneralSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        handleChange({ target: { name: 'formData', value: tempFormData } }); // Restore previous values
    };

    const languageOptions = worldLanguages.map(language => ({ value: language, label: language }));

    // validators
    const validateCountryName = (country) => {
        if (!country || !country.id || !String(country.name || '').trim()) {
            return 'Country name is required.';
        }
        return '';
    };

    const validateCitizensNumber = (val) => {
        if (val === '' || val == null) return 'Number of citizens is required.';
        if (Number.isNaN(Number(val))) return 'Enter a valid number.';
        if (Number(val) < 0) return 'Value cannot be negative.';
        if (!Number.isInteger(Number(val))) return 'Enter a whole number.';
        return '';
    };

    const validateLanguagesSpoken = (vals) => {
        if (!Array.isArray(vals)) return 'Select at least one language';
        const cleaned = vals.map(v => (v == null ? '' : String(v).trim())).filter(Boolean);
        return cleaned.length === 0 ? 'Select at least one language' : '';
    };

    const validateCountryUrl = (v) => !v?.trim() ? 'Country website is required.' : (validateURL(v) ? '' : 'Please enter a valid URL.');

    const validateContinent = (val) => {
        const v = String(val ?? '').trim();
        return v ? '' : 'Continent is required.';
    };

    const validateCurrency = (val, list) => {
        const v = String(val ?? '').trim();
        if (!v) return 'Currency is required.';
        const allowed = new Set((list || []).map(x => x?.value ?? x));
        return allowed.has(v) ? '' : 'Please pick a valid currency.';
    };

    const touchAndValidateLanguages = () => {
        const msg = validateLanguagesSpoken(formData?.languagesSpoken);
        setTouched(t => ({ ...t, languagesSpoken: true }));
        setLocalErrors(e => ({ ...e, languagesSpoken: msg }));
        return !msg; // true if valid
    };

    // ✅ expose validator to parent
    useImperativeHandle(ref, () => ({
        validateLanguagesSpoken: touchAndValidateLanguages,
    }));

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3>General Info</h3>
                            <hr />
                        </Col>
                    </Row>
                    
                    <Row>
                        {/* Form Fields */}
                        <Col md={12}>
                            {isEditing ? (
                                <>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            {(() => {
                                                const countryErrorMsg = touched.countryName ? localErrors?.countryName : errors?.name;
                                                const countryHasError = !!countryErrorMsg;

                                                return (
                                                    <Form.Group className="validated" controlId="countryName">
                                                        <Form.Label>Country Name <span>*</span></Form.Label>

                                                        <Select
                                                            options={Array.isArray(countryOptions) ? countryOptions : []}
                                                            value={(countryOptions || []).find(opt => String(opt.value) === String(formData?.country?.id))}
                                                            onChange={(selectedOption) => {
                                                                const nextCountry = selectedOption
                                                                    ? { id: selectedOption.value, name: selectedOption.label }
                                                                    : {};

                                                                // ✅ mark touched immediately when user selects/clears
                                                                setTouched(t => ({ ...t, countryName: true }));

                                                                handleChange({ target: { name: 'country', value: nextCountry } });

                                                                // ✅ validate immediately on selection
                                                                const msg = validateCountryName(nextCountry);
                                                                setLocalErrors(e => ({ ...e, countryName: msg }));
                                                            }}
                                                            onBlur={() => {
                                                                // optional: keep blur as a safety net, but doesn't need to control "first touch" anymore
                                                                const msg = validateCountryName(formData?.country);
                                                                setLocalErrors(e => ({ ...e, countryName: msg }));
                                                            }}

                                                            isDisabled={!isEditing}
                                                            className={`react-select ${countryHasError ? 'has-error' : ''}`}
                                                            aria-invalid={countryHasError}
                                                            classNamePrefix="react-select"
                                                            placeholder="Select…"
                                                        />

                                                        {countryHasError && (
                                                            <div className="custom-error-icon-wrapper">
                                                                <img
                                                                    src={icValidationError}
                                                                    alt={countryErrorMsg}
                                                                    className="custom-error-icon"
                                                                />
                                                                <div className="custom-tooltip">
                                                                    {countryErrorMsg}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Form.Group>
                                                );
                                            })()}
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="validated" controlId="citizensNumber">
                                                <Form.Label>Number of Citizens <span>*</span></Form.Label>

                                                {(() => {
                                                    const citizensErrorMsg = touched.citizensNumber ? localErrors?.citizensNumber : errors?.citizensNumber;
                                                    const citizensHasError = !!citizensErrorMsg;

                                                    return (
                                                        <>
                                                            <Form.Control
                                                                type="number"
                                                                name="citizensNumber"
                                                                value={formData.citizensNumber ?? ''}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value;
                                                                    const next = raw === '' ? '' : Number(raw);

                                                                    // ✅ touch immediately on first change
                                                                    setTouched((t) => ({ ...t, citizensNumber: true }));

                                                                    handleChange({ target: { name: 'citizensNumber', value: next } });

                                                                    // ✅ validate immediately
                                                                    const msg = validateCitizensNumber(next);
                                                                    setLocalErrors((e2) => ({ ...e2, citizensNumber: msg }));
                                                                }}
                                                                onBlur={() => {
                                                                    // optional safety net (kept)
                                                                    const msg = validateCitizensNumber(formData?.citizensNumber);
                                                                    setLocalErrors((e2) => ({ ...e2, citizensNumber: msg }));
                                                                }}
                                                                min="0"
                                                                step="1"
                                                                inputMode="numeric"
                                                                disabled={!isEditing}
                                                                isInvalid={citizensHasError}
                                                                aria-invalid={citizensHasError}
                                                                placeholder="e.g., 1,350,000"
                                                            />

                                                            {citizensHasError && (
                                                                <div className="custom-error-icon-wrapper">
                                                                    <img
                                                                        src={icValidationError}
                                                                        alt={citizensErrorMsg}
                                                                        className="custom-error-icon"
                                                                    />
                                                                    <div className="custom-tooltip">
                                                                        {citizensErrorMsg}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </Form.Group>

                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group className="validated" controlId="languagesSpoken">
                                                <Form.Label>Languages Spoken <span>*</span></Form.Label>
                                                {(() => {
                                                    const languagesErrorMsg = touched.languagesSpoken ? localErrors?.languagesSpoken : errors?.languagesSpoken;
                                                    const languagesHasError = !!languagesErrorMsg;

                                                    return (
                                                        <>
                                                            <Select
                                                                options={Array.isArray(languageOptions) ? languageOptions : []}
                                                                value={(Array.isArray(formData.languagesSpoken) ? formData.languagesSpoken : [])
                                                                    .map(val => (languageOptions || []).find(opt => String(opt.value) === String(val)))
                                                                    .filter(Boolean)}
                                                                onChange={(selected) => {
                                                                    const next = Array.isArray(selected) ? selected.map(s => s.value) : [];

                                                                    // ✅ touch immediately on select/clear
                                                                    setTouched(t => ({ ...t, languagesSpoken: true }));

                                                                    handleChange({ target: { name: 'languagesSpoken', value: next } });

                                                                    // ✅ validate immediately
                                                                    const msg = validateLanguagesSpoken(next);
                                                                    setLocalErrors(e => ({ ...e, languagesSpoken: msg }));
                                                                }}
                                                                onBlur={() => {
                                                                    // optional safety net (kept)
                                                                    const msg = validateLanguagesSpoken(formData?.languagesSpoken);
                                                                    setLocalErrors(e => ({ ...e, languagesSpoken: msg }));
                                                                }}
                                                                isMulti
                                                                isClearable
                                                                className={`react-select ${languagesHasError ? 'has-error' : ''}`}
                                                                placeholder="Select…"
                                                                classNamePrefix="react-select"
                                                                aria-invalid={languagesHasError}
                                                            />

                                                            {languagesHasError && (
                                                                <div className="custom-error-icon-wrapper">
                                                                    <img
                                                                        src={icValidationError}
                                                                        alt={languagesErrorMsg}
                                                                        className="custom-error-icon"
                                                                    />
                                                                    <div className="custom-tooltip">
                                                                        {languagesErrorMsg}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}

                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="validated" controlId="countryUrl">
                                                <Form.Label>Country URL <span>*</span></Form.Label>

                                               {(() => {
                                                    const urlErrorMsg = touched.url ? localErrors?.url : errors?.url;
                                                    const urlHasError = !!urlErrorMsg;

                                                    return (
                                                        <>
                                                            <Form.Control
                                                                type="text"
                                                                name="url"
                                                                value={urlInput}
                                                                onChange={(e) => {
                                                                    // ✅ touch immediately on first typing
                                                                    setTouched(t => ({ ...t, url: true }));

                                                                    handleURLChange(e);

                                                                    // ✅ validate immediately (live)
                                                                    const msg = validateCountryUrl(e.target.value);
                                                                    setLocalErrors(er => ({ ...er, url: msg }));
                                                                }}
                                                                onBlur={() => {
                                                                    // optional safety net (kept)
                                                                    const msg = validateCountryUrl(urlInput);
                                                                    setLocalErrors(er => ({ ...er, url: msg }));
                                                                }}
                                                                inputMode="url"
                                                                placeholder="example.com"
                                                                disabled={!isEditing}
                                                                isInvalid={urlHasError}
                                                                aria-invalid={urlHasError}
                                                            />

                                                            {urlHasError && (
                                                                <div className="custom-error-icon-wrapper">
                                                                    <img
                                                                        src={icValidationError}
                                                                        alt={urlErrorMsg}
                                                                        className="custom-error-icon"
                                                                    />
                                                                    <div className="custom-tooltip">
                                                                        {urlErrorMsg}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group className="validated" controlId="currency">
                                                <Form.Label>Currency <span>*</span></Form.Label>

                                                {(() => {
                                                    const currencyErrorMsg = touched.currency ? localErrors?.currency : errors?.currency;
                                                    const currencyHasError = !!currencyErrorMsg;

                                                    return (
                                                        <>
                                                            <Form.Select
                                                                name="currency"
                                                                value={formData.currency ?? ''}
                                                                onChange={(e) => {
                                                                    // ✅ touch immediately on first change
                                                                    setTouched(t => ({ ...t, currency: true }));

                                                                    handleChange(e);

                                                                    // ✅ validate immediately
                                                                    const msg = validateCurrency(e.target.value, currencies);
                                                                    setLocalErrors(er => ({ ...er, currency: msg }));
                                                                }}
                                                                onBlur={() => {
                                                                    // optional safety net (kept)
                                                                    const msg = validateCurrency(formData.currency, currencies);
                                                                    setLocalErrors(er => ({ ...er, currency: msg }));
                                                                }}
                                                                disabled={!isEditing}
                                                                isInvalid={currencyHasError}
                                                                aria-invalid={currencyHasError}
                                                                className={currencyHasError ? 'is-invalid' : ''}
                                                            >
                                                                <option value="" disabled>Select</option>
                                                                {Array.isArray(currencies) &&
                                                                    Array.from(
                                                                        new Map(
                                                                            currencies.map(opt => {
                                                                                const value = opt?.value ?? opt;
                                                                                const label = opt?.label ?? opt;
                                                                                return [String(value), { value, label }];
                                                                            })
                                                                        ).values()
                                                                    ).map(({ value, label }) => (
                                                                        <option key={String(value)} value={value}>
                                                                            {value} - {label}
                                                                        </option>
                                                                    ))
                                    }
                                                            </Form.Select>

                                                            {currencyHasError && (
                                                                <div className="custom-error-icon-wrapper">
                                                                    <img
                                                                        src={icValidationError}
                                                                        alt={currencyErrorMsg}
                                                                        className="custom-error-icon"
                                                                    />
                                                                    <div className="custom-tooltip">
                                                                        {currencyErrorMsg}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </Form.Group>

                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="validated" controlId="continent">
                                                <Form.Label>Continent <span>*</span></Form.Label>

                                                {(() => {
                                                    const continentErrorMsg = touched.continent ? localErrors?.continent : errors?.continent;
                                                    const continentHasError = !!continentErrorMsg;

                                                    return (
                                                        <>
                                                            <Select
                                                                options={Array.isArray(continents) ? continents : []}
                                                                value={(continents || []).find(opt => String(opt.value) === String(formData?.continent))}
                                                                onChange={(selectedOption) => {
                                                                    const next = selectedOption ? selectedOption.value : '';

                                                                    // ✅ touch immediately on select/clear
                                                                    setTouched(t => ({ ...t, continent: true }));

                                                                    handleChange({ target: { name: 'continent', value: next } });

                                                                    // ✅ validate immediately
                                                                    const msg = validateContinent(next);
                                                                    setLocalErrors(e => ({ ...e, continent: msg }));
                                                                }}
                                                                onBlur={() => {
                                                                    // optional safety net (kept)
                                                                    const msg = validateContinent(formData?.continent);
                                                                    setLocalErrors(e => ({ ...e, continent: msg }));
                                                                }}
                                                                isDisabled={!isEditing}
                                                                placeholder="Select a continent"
                                                                className={`react-select ${continentHasError ? 'has-error' : ''}`}
                                                                aria-invalid={continentHasError}
                                                                classNamePrefix="react-select"
                                                            />

                                                            {continentHasError && (
                                                                <div className="custom-error-icon-wrapper">
                                                                    <img
                                                                        src={icValidationError}
                                                                        alt={continentErrorMsg}
                                                                        className="custom-error-icon"
                                                                    />
                                                                    <div className="custom-tooltip">
                                                                        {continentErrorMsg}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}

                                            </Form.Group>

                                        </Col>
                                    </Row>
                                </>
                            ) : (
                                <>
                                    
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icCity} alt="Currency" />
                                                </span>
                                                <span className="text">
                                                    Country Name
                                                    <br />
                                                    <strong>
                                                        {formData.country.name || "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icNumber} alt="Number of Citizens" />
                                                </span>
                                                <span className="text">
                                                    Number of Citizens
                                                    <br />
                                                    <strong>
                                                        {formData.citizensNumber || "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icFlag} alt="Language(s) spoken" />
                                                </span>
                                                <span className="text">
                                                    <span className="d-flex">
                                                        Language(s) spoken
                                                    </span>
                                                    <strong>
                                                        {Array.isArray(formData.languagesSpoken) && formData.languagesSpoken.length
                                                            ? formData.languagesSpoken
                                                                .map(v => (languageOptions || []).find(o => String(o.value) === String(v))?.label || v)
                                                                .join(', ')
                                                            : 'N/A'
                                                        }
                                                     </strong>
                                                </span>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icGlobe} alt="Continent allocation" />
                                                </span>
                                                <span className="text">
                                                    <span className="d-flex">
                                                        Continent allocation
                                                    </span>
                                                    <strong>
                                                        {formData.continent || "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icFlag} alt="Currency" />
                                                </span>
                                                <span className="text">
                                                    <span className="d-flex">
                                                        Currency
                                                    </span>
                                                    <strong>
                                                        {formData.currency || "N/A"}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icLink} alt="Country URL" />
                                                </span>
                                                <span className="text">
                                                    <span className="d-flex">
                                                        Country URL
                                                    </span>
                                                    <strong>
                                                        {safeHref ? (
                                                            <a href={safeHref} target="_blank" rel="noopener noreferrer">
                                                                {formData.url}
                                                            </a>
                                                        ) : (
                                                            "N/A"
                                                        )}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>    
        </>
    );
});

export default CountryAddGeneral;
